import { type CSSProperties, type ReactNode, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslations } from "@/hooks/use-translations";
import { cn } from "@/lib/utils";
import { ProtectedRoute } from "@/components/providers/auth-provider";
import { Sidebar } from "@/components/layout/sidebar";
import {
	Search,
	User,
	LogOut,
	ChevronDown,
	Users,
	Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { useTeams, useActiveTeam } from "@/hooks/use-team";
import { AnimatePresence, motion } from "motion/react";
import { NotificationCenter } from "@/components/notifications/notification-center";

// Pages that fill the whole content area themselves (an embedded app), so the
// shell drops its padding and gives them exactly the height under the top bar.
const FULL_BLEED_ROUTES = ["/out-of-home"];

interface DashboardShellProps {
	children: ReactNode;
}

export function DashboardShell({ children }: DashboardShellProps) {
	const t = useTranslations("dashboard");
	const { user } = useAuth();
	const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
	const fullBleed = FULL_BLEED_ROUTES.includes(useLocation().pathname);

	// Team states
	const [showTeamDropdown, setShowTeamDropdown] = useState(false);

	// Active team from session
	const {
		activeTeam,
		setActiveTeam,
		isLoading: isLoadingActiveTeam,
	} = useActiveTeam();
	const { data: teams = [], isLoading: isLoadingTeams } = useTeams();

	const handleSelectTeam = async (teamId: string) => {
		try {
			await setActiveTeam(teamId);
			setShowTeamDropdown(false);
		} catch (error) {
			console.error("Failed to set active team:", error);
		}
	};

	const handleWorkIndividually = async () => {
		try {
			await setActiveTeam(null);
			setShowTeamDropdown(false);
		} catch (error) {
			console.error("Failed to clear active team:", error);
		}
	};

	return (
		<ProtectedRoute>
			<div
				className="min-h-screen bg-bg-canvas flex"
				style={
					{ "--sidebar-width": sidebarCollapsed ? "72px" : "240px" } as CSSProperties
				}
			>
				<Sidebar
					collapsed={sidebarCollapsed}
					onToggle={() => setSidebarCollapsed((collapsed) => !collapsed)}
				/>
				<div
					className={cn(
						"flex-1 min-w-0 flex flex-col transition-[margin-left] duration-200 motion-reduce:transition-none",
						fullBleed ? "h-screen" : "min-h-screen",
					)}
					style={{ marginLeft: "var(--sidebar-width)" }}
				>
					{/* Top Bar */}
					<header className="sticky top-0 z-30 border-b bg-bg-surface border-border-subtle backdrop-blur">
						<div className="px-6 py-3 sm:px-6 lg:px-8">
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-4 flex-1">
									<div className="relative max-w-md w-full hidden sm:block">
										<Search
											size={16}
											className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary"
										/>
										<input
											type="search"
											placeholder={t("searchPlaceholder")}
											className="w-full h-9 pl-9 pr-4 text-sm rounded-xl border border-border-control bg-bg-surface text-text-primary placeholder:text-text-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg-surface"
										/>
									</div>
								</div>

								<div className="flex items-center gap-2">
									<NotificationCenter />

									<div className="h-6 w-px bg-border-subtle mx-2 hidden sm:block" />

									{/* Team & User Selector */}
									<div className="relative">
										<button
											onClick={() => setShowTeamDropdown(!showTeamDropdown)}
											className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-bg-canvas transition-colors"
										>
											<div className="w-8 h-8 rounded-full bg-bg-sunken flex items-center justify-center flex-shrink-0">
												{activeTeam ? (
													<Users size={16} className="text-text-secondary" />
												) : (
													<User size={16} className="text-text-secondary" />
												)}
											</div>
											<div className="hidden sm:flex flex-col items-start">
												<span className="text-sm font-medium text-text-primary">
													{activeTeam?.name || user?.name || t("noTeamsYet")}
												</span>
												<span className="text-xs text-text-tertiary">
													{activeTeam ? t("team.label") : user?.email}
												</span>
											</div>
											<ChevronDown size={16} className="text-text-tertiary" />
										</button>

										{/* Dropdown Menu */}
										<AnimatePresence>
											{showTeamDropdown && (
												<>
													<div
														className="fixed inset-0 z-40"
														onClick={() => setShowTeamDropdown(false)}
													/>
													<motion.div
														initial={{ opacity: 0, y: -10 }}
														animate={{ opacity: 1, y: 0 }}
														exit={{ opacity: 0, y: -10 }}
														className="absolute right-0 top-full mt-2 w-72 rounded-xl bg-bg-surface shadow-popover border border-border-subtle z-50 overflow-hidden"
													>
														<div className="p-3 border-b border-border-subtle">
															<p className="text-xs font-medium text-text-tertiary uppercase tracking-wider">
																{t("team.yourTeams")}
															</p>
														</div>
														<div className="max-h-64 overflow-y-auto">
															{/* Personal Account Option */}
															<button
																onClick={handleWorkIndividually}
																className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-bg-canvas transition-colors"
															>
																<div className="flex items-center gap-3">
																	<div className="w-8 h-8 rounded-full bg-bg-sunken flex items-center justify-center">
																		<User size={14} className="text-text-tertiary" />
																	</div>
																	<div className="flex flex-col items-start">
																		<span className="text-sm font-medium text-text-primary">
																			{t("team.personal")}
																		</span>
																		<span className="text-xs text-text-tertiary">
																			{user?.email}
																		</span>
																	</div>
																</div>
																{!activeTeam && (
																	<Check size={16} className="text-text-primary" />
																)}
															</button>

															{/* Teams List */}
															{teams.length === 0 ? (
																<div className="p-4 text-center border-t border-border-subtle">
																	<Users
																		size={32}
																		className="text-text-tertiary mx-auto mb-2"
																	/>
																	<p className="text-sm text-text-tertiary">
																		{t("noTeamsYet")}
																	</p>
																</div>
															) : (
																<>
																	<div className="h-px bg-border-subtle" />
																	{teams.map((team) => (
																		<button
																			key={team.id}
																			onClick={() => handleSelectTeam(team.id)}
																			className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-bg-canvas transition-colors"
																		>
																			<div className="flex items-center gap-3">
																				<div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center">
																					<Users
																						size={14}
																						className="text-text-primary"
																					/>
																				</div>
																				<div className="flex flex-col items-start">
																					<span className="text-sm font-medium text-text-primary">
																						{team.name}
																					</span>
																					<span className="text-xs text-text-tertiary">
																						{team.slug}
																					</span>
																				</div>
																			</div>
																			{activeTeam?.id === team.id && (
																				<Check
																					size={16}
																					className="text-text-primary"
																				/>
																			)}
																		</button>
																	))}
																</>
															)}
														</div>

														{/* Creating teams is on the Manager page (Super Admin only). */}
													</motion.div>
												</>
											)}
										</AnimatePresence>
									</div>
								</div>
							</div>
						</div>
					</header>

					{/* Main Content */}
					<main
						className={
							fullBleed
								? "flex min-h-0 flex-1 flex-col"
								: "flex-1 overflow-auto pt-8 px-6"
						}
					>
						{children}
					</main>
				</div>
			</div>
		</ProtectedRoute>
	);
}
